import { type AxiosProgressEvent, isCancel } from "axios";
import { axiosClient } from "~/js/network/axiosClient";
import type { nodeNewExecutor } from "~/js/utilities/changeNodeExecutorOnCustom";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getPublicMessageFromError, type IFileType } from "~/solidJs/shared";
import { BaseApi } from "~/solidJs/shared/api/baseApi";
import {
	collectNodeDataForTask,
	findSocketInParams,
	prepareFileToShowInNode
} from "~/solidJs/shared/helpers/nodeHelpers";
import { ExecutionStatus } from "../../node-executor";

class UploadApi extends BaseApi {
	static baseUrl = `${this.baseUrlV2}/nodes/upload_lora_model`;
	static async uploadModel(props: {
		model_type: string;
		model: IFileType;
		name: string;
		onProgress?: (progressEvent: AxiosProgressEvent) => void;
		signal?: AbortSignal;
	}): Promise<string> {
		const formData = new FormData();
		formData.append("file", props.model, props.model.name);
		formData.append("token", props.name);
		formData.append("description", "string");
		formData.append("link_to_model", "string");

		formData.append("Content-Type", "multipart/form-data");
		const response = await axiosClient.axiosClient.post(UploadApi.baseUrl, formData, {
			headers: {
				"Content-Type": "multipart/form-data"
			},
			params: { model_type: props.model_type },
			signal: props.signal,
			onUploadProgress: props.onProgress
		});
		return response.data;
	}
}
const nodeTypeToSuccess: Record<string, string> = {
	FLUX: "Flux",
	SDXL: "SD XL"
};
export const uploadModel: nodeNewExecutor = (node, layout, initialExecutor) => {
	initialExecutor(node, layout);
	const executor = node.executuionBlock;
	// @ts-expect-error
	executor.getStartText = () => "Start Uploading";

	executor.setExecuteFunction(async (cancelController: AbortController) => {
		executor.DOMStyle.executeBlock.start("Loading");
		try {
			const data = await collectNodeDataForTask(node, executor);
			const model = findSocketInParams(data.inputs, "model");
			const name = findSocketInParams(data.inputs, "name");
			const modelType = findSocketInParams(data.params, "model_type");
			if (!model || !name || !modelType) {
				throw new Error("No name or model was provided");
			}
			executor.updateStatus(ExecutionStatus.Starting, "uploading the model...");
			const file = model.value as IFileType;
			const maxFileSizeInBytes = 1024 * 1024 * 1024; // 1GB
			if (file.size > maxFileSizeInBytes) {
				throw new Error(
					"The selected file is too large. Please upload a file smaller than 1GB."
				);
			}
			EventAnalytics.uploadModelStart();
			await UploadApi.uploadModel({
				model_type: modelType.value as string,
				model: file,
				name: name.value as string,
				signal: cancelController.signal,
				onProgress(event) {
					if (!event.total) return;
					executor.updateStatus(
						ExecutionStatus.Starting,
						`loading the model: ${Math.round((event.loaded / event.total) * 100)}%`
					);
				}
			});

			prepareFileToShowInNode("/public/SuccessUploadLora.png").then((outputs) =>
				node.updateOutputs(outputs)
			);
			executor.updateStatus(
				ExecutionStatus.Done,
				`Successful upload! Create new ${nodeTypeToSuccess[modelType.value as string]} node to start creating!`
			);
		} catch (e) {
			if (isCancel(e)) {
				return;
			}
			executor.updateStatus(
				ExecutionStatus.Error,
				getPublicMessageFromError(e),
				"error_params"
			);
			executor.DOMStyle.executeBlock.finish("Upload failed");
			return;
		}
		executor.DOMStyle.executeBlock.finish("Upload finished");
	});
};
